import type {UtmConfig} from './utm-config-editor';
import type {StaticUtmParamKey, UtmConfigEditorState} from './utm-config-form';
import uniqueId from 'lodash/uniqueId';


export const STATIC_UTM_PARAM_KEYS = [
  'utm_campaign',
  'utm_term',
  'utm_source',
] as const;

export const UTM_PARAMETER_LABELS = {
  utm_campaign: 'UTM Campaign',
  utm_term: 'UTM Term',
  utm_source: 'UTM Source',
  utm_medium: 'UTM Medium',
};

export function getUtmParameterLabel(utmParamKey: string): string {
  return (
    UTM_PARAMETER_LABELS[utmParamKey as keyof typeof UTM_PARAMETER_LABELS] ??
    utmParamKey
  );
}

export function getStaticUtmParamValue(
  utmConfigEditorState: UtmConfigEditorState,
  key: StaticUtmParamKey,
): string {
  return (
    utmConfigEditorState.utmParams.find((utmParam) => utmParam.key === key)
      ?.value ?? ''
  );
}

export function parseUTMConfig(
  utmConfig: UtmConfig | null,
): UtmConfigEditorState {
  const params = utmConfig?.params ?? {};

  const staticUtmRows = STATIC_UTM_PARAM_KEYS.map((key) => ({
    id: key,
    key,
    value: params[key] ?? '',
    isStatic: true,
  }));

  const customUtmRows = Object.entries(params)
    .filter(
      ([key]) => !STATIC_UTM_PARAM_KEYS.includes(key as StaticUtmParamKey),
    )
    .map(([key, value]) => ({
      id: uniqueId('utmParamKey'),
      key,
      value,
      isStatic: false,
    }));

  return {
    includeUtmMedium: utmConfig?.include_utm_medium ?? false,
    utmParams: [...staticUtmRows, ...customUtmRows],
  };
}

export function normalizeUTMConfig(
  utmConfigEditorState: UtmConfigEditorState,
): UtmConfig {
  const includeUtmMedium = utmConfigEditorState.includeUtmMedium;
  const params = utmConfigEditorState.utmParams.reduce<Record<string, string>>(
    (params, utmRow) => {
      const trimmedKey = utmRow.key.trim();
      const trimmedValue = utmRow.value.trim();

      if (!trimmedKey || !trimmedValue || trimmedKey === 'utm_medium') {
        return params;
      }

      params[trimmedKey] = trimmedValue;
      return params;
    },
    {},
  );

  return {
    include_utm_medium: includeUtmMedium,
    ...(Object.keys(params).length > 0 ? {params} : {}),
  };
}

export function isUTMConfigEqual(
  a: UtmConfig | null,
  b: UtmConfig | null,
): boolean {
  const includeUtmMediumA = a?.include_utm_medium ?? false;
  const includeUtmMediumB = b?.include_utm_medium ?? false;

  if (includeUtmMediumA !== includeUtmMediumB) {
    return false;
  }

  const paramsA = a?.params ?? {};
  const paramsB = b?.params ?? {};
  const keysA = Object.keys(paramsA).sort();
  const keysB = Object.keys(paramsB).sort();

  if (keysA.length !== keysB.length) {
    return false;
  }

  return keysA.every((key, index) => {
    return key === keysB[index] && paramsA[key] === paramsB[key];
  });
}
